import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Settings, MessageSquare, User, Plug, Sliders, Zap, CreditCard, Brain } from 'lucide-react';
import apiClient from '../utils/api';
import Navigation from '../components/Navigation';
import { useTranslation } from '../i18n';
import { useConfig } from '../hooks/useConfig';
import './ConfigurationNew.css';

// Import tab components
import PlatformsTab from '../components/config/PlatformsTab';
import PreferencesTab from '../components/config/PreferencesTab';
import ModelsTab from '../components/config/ModelsTab';
import IntegrationsTab from '../components/config/IntegrationsTab';
import SubscriptionTab from '../components/config/SubscriptionTab';
import MemoryTab from '../components/config/MemoryTab';

const Configuration = () => {
  const { t } = useTranslation();
  const { config: appConfig } = useConfig();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('subscription');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    console.log('Config page - URL params:', { search: location.search, tab, currentActiveTab: activeTab });
    if (tab) {
      console.log('Setting active tab to:', tab);
      setActiveTab(tab);
    }
  }, [location.search]);

  const tabs = [
    { id: 'subscription', label: t('subscription.tab'), icon: CreditCard, description: t('subscription.tabDesc') },
    { id: 'integrations', label: t('integrations'), icon: Plug, description: t('integrationsDesc') },
    { id: 'platforms', label: 'WhatsApp', icon: MessageSquare, description: t('platformsDesc') },
    { id: 'preferences', label: t('preferences'), icon: User, description: t('preferencesDesc') },
    { id: 'models', label: t('aiModels'), icon: Zap, description: t('aiModelsDesc') },
    { id: 'memory', label: t('memory'), icon: Brain, description: t('memoryDesc') }
  ];

  return (
    <div className="page-container">
      <Navigation />
      <div className="config-container">
        <div className="config-header">
          <div className="config-header-content">
            <Settings size={32} />
            <div>
              <h1>{t('configuration')}</h1>
              <p>{t('managePlatformsPreferences')}</p>
            </div>
          </div>
        </div>

        <div className="config-layout">
          {isMobile ? (
            <div className="config-sidebar">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <div key={tab.id} className="config-accordion-item">
                    <button
                      className={`config-menu-item ${isActive ? 'active' : ''}`}
                      onClick={() => setActiveTab(isActive ? '' : tab.id)}
                    >
                      <Icon size={20} />
                      <div className="config-menu-content">
                        <span className="config-menu-label">{tab.label}</span>
                        <span className="config-menu-description">{tab.description}</span>
                      </div>
                    </button>
                    {isActive && (
                      <div className="config-accordion-content">
                        {tab.id === 'platforms' && <PlatformsTab />}
                        {tab.id === 'preferences' && <PreferencesTab />}
                        {tab.id === 'models' && <ModelsTab />}
                        {tab.id === 'integrations' && <IntegrationsTab />}
                        {tab.id === 'subscription' && <SubscriptionTab />}
                        {tab.id === 'memory' && <MemoryTab />}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div className="config-sidebar">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      className={`config-menu-item ${isActive ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <Icon size={20} />
                      <div className="config-menu-content">
                        <span className="config-menu-label">{tab.label}</span>
                        <span className="config-menu-description">{tab.description}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="config-content config-content-desktop">
                {activeTab === 'platforms' && <PlatformsTab />}
                {activeTab === 'preferences' && <PreferencesTab />}
                {activeTab === 'models' && <ModelsTab />}
                {activeTab === 'integrations' && <IntegrationsTab />}
                {activeTab === 'subscription' && <SubscriptionTab />}
                {activeTab === 'memory' && <MemoryTab />}
              </div>
            </>
          )}
        </div>

        {/* Version Info Footer */}
        {appConfig?.version && (
          <div style={{ 
            marginTop: '2rem',
            padding: '1rem',
            borderTop: '1px solid #e5e7eb',
            fontSize: '0.75rem',
            color: '#6b7280',
            fontFamily: 'monospace',
            display: 'flex',
            justifyContent: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <span>Version: {appConfig.version.version}</span>
            <span>Build: {appConfig.version.build_date}</span>
            <span>Commit: {appConfig.version.git_commit?.substring(0, 7)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Configuration;
